import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Search, ArrowLeft, UserPlus, Calendar as CalendarIcon, Phone, Mail, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  getLeads, getQuickLeads, addShowing, createLead, moveQuickToCurrent,
} from '@/lib/api';

/**
 * AddShowingDialog
 *
 * Two-step workflow launched from the dashboard:
 *   1. Pick a lead (search across regular + quick leads) OR create a new
 *      regular lead inline.
 *   2. Add a showing (date, time, notes) for that lead. If a Quick Lead
 *      was picked it's auto-promoted to a regular lead first so the
 *      showings collection lives on the lead we can attach shows to.
 *
 * When done, calls `onCreated` so the dashboard can refresh its upcoming
 * showings widget.
 */
export function AddShowingDialog({ open, onOpenChange, onCreated }) {
  // 'pick' → search/create; 'showing' → date/time/notes for chosen lead
  const [step, setStep] = useState('pick');
  const [leads, setLeads] = useState([]);
  const [quickLeads, setQuickLeads] = useState([]);
  const [loadingLeads, setLoadingLeads] = useState(false);
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState(null); // { id, name, phone, email, kind: 'current'|'quick' }
  const [creatingLead, setCreatingLead] = useState(false);
  const [newLead, setNewLead] = useState({ name: '', phone: '', email: '' });
  const [showingForm, setShowingForm] = useState({ date: '', time: '', notes: '' });
  const [saving, setSaving] = useState(false);

  const resetAll = useCallback(() => {
    setStep('pick');
    setQ('');
    setSelected(null);
    setCreatingLead(false);
    setNewLead({ name: '', phone: '', email: '' });
    setShowingForm({ date: '', time: '', notes: '' });
    setSaving(false);
  }, []);

  useEffect(() => {
    if (!open) {
      resetAll();
      return;
    }
    setLoadingLeads(true);
    Promise.all([
      getLeads().catch(() => []),
      getQuickLeads().catch(() => []),
    ])
      .then(([ls, qs]) => {
        setLeads(ls || []);
        setQuickLeads(qs || []);
      })
      .finally(() => setLoadingLeads(false));
  }, [open, resetAll]);

  const flatLeads = useMemo(() => {
    const norm = (l, kind) => ({
      id: l.id,
      name: l.name || 'Unnamed',
      phone: l.phone || '',
      email: l.email || '',
      kind,
    });
    return [
      ...leads.filter(l => !l.converted_to_tenant).map(l => norm(l, 'current')),
      ...quickLeads.map(l => norm(l, 'quick')),
    ];
  }, [leads, quickLeads]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return flatLeads.slice(0, 30);
    return flatLeads
      .filter(l =>
        l.name.toLowerCase().includes(needle)
        || l.phone.toLowerCase().includes(needle)
        || l.email.toLowerCase().includes(needle),
      )
      .slice(0, 30);
  }, [flatLeads, q]);

  const goToShowingStep = (lead) => {
    setSelected(lead);
    setStep('showing');
  };

  const handleCreateNewLead = async () => {
    if (!newLead.name.trim()) {
      toast.error('Name is required');
      return;
    }
    setSaving(true);
    try {
      // Regular lead — starts at strength=1, no unit constraints.
      const created = await createLead({
        name: newLead.name.trim(),
        source: 'dashboard',
        phone: newLead.phone.trim(),
        email: newLead.email.trim(),
        potential_unit_ids: [],
        notes: '',
        pets: '',
        parking_request: '',
        price_offered: null,
        desired_start_date: null,
        desired_end_date: null,
        lead_strength: 1,
        preferred_contact_method: '',
      });
      toast.success('Lead added');
      goToShowingStep({ id: created.id, name: created.name, phone: created.phone, email: created.email, kind: 'current' });
    } catch {
      toast.error('Failed to create lead');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveShowing = async () => {
    if (!selected) return;
    if (!showingForm.date || !showingForm.time) {
      toast.error('Date and time are required');
      return;
    }
    setSaving(true);
    try {
      let leadId = selected.id;
      // Quick leads don't own showings. Promote to a regular lead first so
      // the showing has a durable home.
      if (selected.kind === 'quick') {
        const moved = await moveQuickToCurrent(selected.id);
        leadId = moved.id;
        toast.success(`${selected.name} moved to current leads`);
      }
      await addShowing(leadId, showingForm);
      toast.success('Showing added');
      onCreated && onCreated();
      onOpenChange(false);
    } catch {
      toast.error('Failed to add showing');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="add-showing-dialog">
        <DialogHeader>
          <div className="flex items-center gap-2">
            {(step === 'showing' || creatingLead) && (
              <button
                onClick={() => {
                  if (creatingLead) { setCreatingLead(false); return; }
                  setStep('pick'); setSelected(null);
                }}
                className="p-1 -ml-1 rounded hover:bg-slate-100 text-slate-500"
                title="Back"
                data-testid="showing-dialog-back"
              >
                <ArrowLeft className="h-4 w-4" />
              </button>
            )}
            <DialogTitle className="font-heading flex items-center gap-2">
              <CalendarIcon className="h-4 w-4 text-teal-600" />
              {step === 'showing'
                ? `Schedule showing for ${selected?.name}`
                : creatingLead
                  ? 'New Showing — Add new lead'
                  : 'New Showing — Pick a lead'}
            </DialogTitle>
          </div>
        </DialogHeader>

        {step === 'pick' ? (
          <div className="space-y-3">
            {!creatingLead ? (
              <>
                <div className="relative">
                  <Search className="h-4 w-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    autoFocus
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Search by name, phone or email…"
                    className="pl-8"
                    data-testid="showing-lead-search"
                  />
                </div>
                <div className="border rounded-lg divide-y max-h-72 overflow-y-auto" data-testid="showing-lead-results">
                  {loadingLeads && (
                    <div className="p-3 text-sm text-slate-500 flex items-center gap-2">
                      <Loader2 className="h-3 w-3 animate-spin" /> Loading leads…
                    </div>
                  )}
                  {!loadingLeads && filtered.length === 0 && (
                    <div className="p-3 text-sm text-slate-500">
                      {q ? 'No leads match your search.' : 'No leads yet.'}
                    </div>
                  )}
                  {!loadingLeads && filtered.map(l => (
                    <button
                      key={`${l.kind}-${l.id}`}
                      onClick={() => goToShowingStep(l)}
                      className="w-full text-left px-3 py-2 hover:bg-slate-50 flex items-center gap-3"
                      data-testid={`showing-lead-option-${l.id}`}
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium truncate">{l.name}</span>
                          <span
                            className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                              l.kind === 'quick'
                                ? 'bg-amber-100 text-amber-800'
                                : 'bg-teal-100 text-teal-800'
                            }`}
                          >
                            {l.kind === 'quick' ? 'Quick' : 'Current'}
                          </span>
                        </div>
                        <div className="text-[11px] text-slate-500 flex items-center gap-3 mt-0.5">
                          {l.phone && <span className="inline-flex items-center gap-1"><Phone className="h-2.5 w-2.5" /> {l.phone}</span>}
                          {l.email && <span className="inline-flex items-center gap-1 truncate"><Mail className="h-2.5 w-2.5" /> {l.email}</span>}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
                <Button
                  variant="outline"
                  className="w-full gap-1.5"
                  onClick={() => setCreatingLead(true)}
                  data-testid="showing-new-lead-btn"
                >
                  <UserPlus className="h-4 w-4" /> Add new lead
                </Button>
              </>
            ) : (
              <div className="space-y-3" data-testid="showing-new-lead-form">
                <p className="text-xs text-slate-600">
                  New lead will be added to <strong>Current Leads</strong>. You can flesh out the rest later.
                </p>
                <div className="space-y-1">
                  <Label className="text-xs">Name *</Label>
                  <Input
                    autoFocus
                    value={newLead.name}
                    onChange={(e) => setNewLead(f => ({ ...f, name: e.target.value }))}
                    placeholder="e.g. Alex Rivera"
                    data-testid="showing-new-lead-name"
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1">
                    <Label className="text-xs">Phone</Label>
                    <Input
                      value={newLead.phone}
                      onChange={(e) => setNewLead(f => ({ ...f, phone: e.target.value }))}
                      placeholder="(305) 555-0102"
                      data-testid="showing-new-lead-phone"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Email</Label>
                    <Input
                      type="email"
                      value={newLead.email}
                      onChange={(e) => setNewLead(f => ({ ...f, email: e.target.value }))}
                      placeholder="alex@example.com"
                      data-testid="showing-new-lead-email"
                    />
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    className="w-full bg-teal-700 hover:bg-teal-800"
                    onClick={handleCreateNewLead}
                    disabled={saving || !newLead.name.trim()}
                    data-testid="showing-new-lead-save"
                  >
                    {saving ? 'Adding…' : 'Add & continue to showing'}
                  </Button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {selected?.kind === 'quick' && (
              <div className="text-[11px] rounded border border-amber-200 bg-amber-50 text-amber-900 px-2.5 py-1.5">
                {selected.name} is currently a Quick Lead. Adding a showing will move them to Current Leads.
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Date *</Label>
                <Input
                  type="date"
                  value={showingForm.date}
                  onChange={(e) => setShowingForm(f => ({ ...f, date: e.target.value }))}
                  data-testid="showing-add-date"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Time *</Label>
                <Input
                  type="time"
                  value={showingForm.time}
                  onChange={(e) => setShowingForm(f => ({ ...f, time: e.target.value }))}
                  data-testid="showing-add-time"
                />
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Notes</Label>
              <Textarea
                rows={2}
                value={showingForm.notes}
                onChange={(e) => setShowingForm(f => ({ ...f, notes: e.target.value }))}
                placeholder="Optional — meeting notes, unit(s) to show, etc."
                data-testid="showing-add-notes"
              />
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          {step === 'showing' && (
            <Button
              onClick={handleSaveShowing}
              disabled={saving || !showingForm.date || !showingForm.time}
              className="bg-teal-700 hover:bg-teal-800"
              data-testid="showing-add-save"
            >
              {saving ? 'Saving…' : 'Add showing'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default AddShowingDialog;
